/**
 * O endereço e o fechamento do WebSocket do portal (D3, passo 19 — PR 2). Passados ao `useRealtimeNotifications` pelo `PortalAoVivo`.
 * 🔴 Nunca o `/api/v1/ws/notifications` do interno: o token do parceiro é recusado lá (o conserto de segurança do §10) e o do portal
 * recusa o interno aqui (4003 — o PR 1).
 *
 * Os códigos (PR 1, Decisão 3): token e pessoa recusados → o servidor ACEITA e fecha com o código, para o navegador LER (4001, 4003);
 * Origin estranha → recusa no handshake (HTTP 403, o navegador vê 1006), tratada como qualquer queda: reconecta com o recuo crescente do
 * transporte, sem SSE.
 */
import { getWsUrl } from "@/config/api";
import { garantirTokenFresco, renovarTokenDoPortal } from "@/lib/portal/tokenDoPortal";
import type { DecisaoDoFechamento, InfoDoFechamento } from "@/lib/realtime-transport";
import { portalStorage } from "@/services/portalApi";

export const CAMINHO_DO_WS_DO_PORTAL = "/api/v1/portal/ws/notifications";

/** O endereço, lido a CADA tentativa: o token vence em 15 min. Vazio = ainda não dá (o transporte conta como falha e tenta de novo). */
export async function urlDoWsDoPortal(): Promise<string> {
  if (!(await garantirTokenFresco())) return "";
  const token = portalStorage.getAccessToken();
  return token ? getWsUrl(`${CAMINHO_DO_WS_DO_PORTAL}?token=${encodeURIComponent(token)}`) : "";
}

/** Dois 4001 dentro deste intervalo = o token RENOVADO também foi recusado: o ciclo normal (o token vence) tem 15 min. */
export const JANELA_DO_LACO_DE_4001_MS = 60_000;

let ultimo4001Em: number | null = null;

/**
 * 4003 (token que não é de parceiro): não adianta insistir. 4001 (token ilegível, vencido, revogado, pessoa recusada — ou o token venceu
 * com o socket aberto): renova e reconecta; refresh recusado = a sessão acabou (o `renovarTokenDoPortal` já mandou ao login). 🔴 Um 4001 que
 * se repete em menos de 60 s significa que o servidor recusa a pessoa MESMO com o token novo (inativa, não é parceira): sem esta
 * desistência seria um refresh e um handshake a cada 3 s para sempre. O resto (1006, queda de rede, deploy, Origin): reconecta — o recuo é
 * do transporte.
 */
export async function decisaoAoFechar(
  info: InfoDoFechamento,
  agora: () => number = Date.now
): Promise<DecisaoDoFechamento> {
  if (info.code === 4003) return "parar";
  if (info.code === 4001) {
    const t = agora();
    const emLaco = ultimo4001Em !== null && t - ultimo4001Em < JANELA_DO_LACO_DE_4001_MS;
    ultimo4001Em = t;
    if (emLaco) return "parar";
    return (await renovarTokenDoPortal()) === "recusado" ? "parar" : "reconectar";
  }
  return "reconectar";
}

/** Só para os testes. */
export function _reiniciarSocketDoPortalParaTeste(): void {
  ultimo4001Em = null;
}
